import { createFileRoute, useParams } from '@tanstack/react-router';
import { isPreparingLesson, lessonPrepLabel, type LessonPrep } from '@/components/Learn/lessonPrep';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WORKERS } from '@/api/config';
import { useAutoStartSession } from '@/hooks/useAutoStartSession';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Clock,
  BookOpen,
  Play,
  CheckCircle2,
  ChevronLeft,
  MoreVertical,
  Trash2,
  Share2,
  Eye,
  RotateCcw,
  EyeOff,
  Globe,
  Pencil,
} from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getMyEnrollmentsOptions, deleteSessionGroupMutation, getCandidatePublicPublicCandidatesApplicationIdGetOptions } from '@/api/generated/@tanstack/react-query.gen';
import { useAuth, getCurrentToken } from '@/lib/auth';
import toast from 'react-hot-toast';
import { CandidateDetailsSheet } from '@/scenes/Candidates/CandidateDetailsSheet';
import { createPortal } from 'react-dom';
import { useCustomer } from 'autumn-js/react';
import { canStartSession } from '@/lib/plan';
import { trackLearningPathStart } from '@/lib/analytics';
import { useSessionMaterialsStatus, MaterialsSlider } from '@/components/Learn/MaterialsSlider';
import { MaterialsStatusBanner } from '@/components/Learn/MaterialsStatusBanner';
import { CoverImageUpload } from '@/components/Learn/CoverImageUpload';
import { StartLearningButton } from '@/components/Learn/StartLearningButton';

const { USERS_WORKER } = WORKERS;

export const Route = createFileRoute('/_auth/learn/path/$id')({
  component: LearningPathPage,
});

interface SessionItem extends LessonPrep {
  id: string;
  name: string;
  duration: number;
  description: string;
}

interface SessionGroupDetail {
  id: string;
  name: string;
  description: string;
  type?: 'learning' | 'screening' | 'research';
  cover_image_url: string | null;
  learning_outcomes: string[];
  categories: string[];
  total_duration_minutes: number;
  session_count: number;
  generation_status: string;
  generated_session_count: number;
  target_session_count: number;
  public_id: string;
  sessions: SessionItem[];
  avg_rating: number | null;
  enrollment_count: number;
  owner_id: string | null;
  is_published: boolean;
}

interface Enrollment {
  _id: string;
  session_group_id: string;
  group_name: string;
  completed_session_template_ids: string[];
  session_template_ids: string[];
  total_sessions: number;
  completed_sessions: number;
}

async function fetchGroupDetail(identifier: string): Promise<SessionGroupDetail> {
  const res = await fetch(`${USERS_WORKER}/public/learn/session-groups/${identifier}`);
  if (!res.ok) throw new Error('Failed to fetch');
  return res.json();
}

function GeneratingHeroOverlay() {
  return (
    <div className="absolute inset-0 overflow-hidden bg-gradient-to-br from-zinc-100 via-white to-primary/10">
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent"
        initial={{ x: '-100%' }}
        animate={{ x: '100%' }}
        transition={{ duration: 2.2, repeat: Infinity, ease: 'linear' }}
      />

      <div className="absolute right-4 top-4">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/85 px-3 py-1.5 text-xs font-medium text-zinc-700 shadow-sm backdrop-blur-sm">
          <span className="h-1.5 w-1.5 rounded-full bg-zinc-400 animate-pulse" />
          Generating cover image
        </div>
      </div>
    </div>
  );
}




function LearningPathPage() {
  const { id } = useParams({ from: '/_auth/learn/path/$id' });
  const navigate = useNavigate();
  const { startSession, isStarting, startingSessionId, isCustomerLoading } = useAutoStartSession();
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [viewingApplicationId, setViewingApplicationId] = useState<string | null>(null);
  const [loadingResultFor, setLoadingResultFor] = useState<string | null>(null);
  const [loadedCoverImageUrl, setLoadedCoverImageUrl] = useState<string | null>(null);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { customer } = useCustomer();
  const hasCredits = !isCustomerLoading && canStartSession(customer);

  const { data: viewingCandidate } = useQuery({
    ...getCandidatePublicPublicCandidatesApplicationIdGetOptions({
      path: { application_id: viewingApplicationId || '' },
    }),
    enabled: !!viewingApplicationId,
  });

  const handleViewResults = async (sessionTemplateId: string) => {
    setLoadingResultFor(sessionTemplateId);
    try {
      const token = await getCurrentToken();
      const res = await fetch(`${USERS_WORKER}/learn/my-session-result/${sessionTemplateId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('No results');
      const data = await res.json();
      if (data?.application_id) {
        setViewingApplicationId(data.application_id);
      } else {
        toast('Results not available yet');
      }
    } catch {
      toast('Results not available yet');
    } finally {
      setLoadingResultFor(null);
    }
  };

  const visibilityMutation = useMutation({
    mutationFn: async ({ groupId, isPublished }: { groupId: string; isPublished: boolean }) => {
      const token = await getCurrentToken();
      const res = await fetch(`${USERS_WORKER}/learn/session-group/${groupId}/visibility`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ is_published: isPublished }),
      });
      if (!res.ok) throw new Error('Failed to update visibility');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['learn-group-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['learn-session-groups'] });
      toast.success(data.is_published ? 'Visible on explore page' : 'Hidden from explore page');
    },
    onError: () => {
      toast.error('Failed to update visibility');
    },
  });

  const deleteMutation = useMutation({
    ...deleteSessionGroupMutation(),
    onSuccess: () => {
      toast.success('Learning path deleted');
      queryClient.invalidateQueries({ queryKey: ['learn-session-groups'] });
      queryClient.invalidateQueries({ queryKey: ['getMyEnrollments'] });
      navigate({ to: '/dashboard' });
    },
    onError: () => {
      toast.error('Failed to delete');
    },
  });

  const generatingStartRef = useRef<number | null>(null);
  const MAX_POLL_MS = 5 * 60 * 1000;

  const { data: group, isLoading } = useQuery({
    queryKey: ['learn-group-detail', id],
    queryFn: () => fetchGroupDetail(id),
    refetchInterval: (query) => {
      const status = query.state.data?.generation_status;
      if (status === 'planning' || status === 'generating') {
        if (!generatingStartRef.current) {
          generatingStartRef.current = Date.now();
        }
        if (Date.now() - generatingStartRef.current > MAX_POLL_MS) {
          return false;
        }
        return 3000;
      }
      generatingStartRef.current = null;
      if ((query.state.data?.sessions || []).some(isPreparingLesson)) return 2000;
      return false;
    },
  });

  const { data: enrollments } = useQuery({
    ...getMyEnrollmentsOptions(),
    staleTime: 0,
  });

  // Find current enrollment to check completed sessions
  const enrollment = Array.isArray(enrollments)
    ? (enrollments as Enrollment[]).find((e) => e.session_group_id === group?.id)
    : null;

  const completedIds = new Set(enrollment?.completed_session_template_ids || []);
  const isPlanning = group?.generation_status === 'planning';
  const isGenerating = group?.generation_status === 'generating';
  const isFailed = group?.generation_status === 'failed';
  const isInProgress = isPlanning || isGenerating;
  const useSoftHero = isInProgress && !group?.cover_image_url;

  const materialsStatus = useSessionMaterialsStatus(group?.sessions || [], {
    expectMaterials: !isFailed && (group?.sessions?.length ?? 0) > 0,
    groupGenerating: isInProgress,
    groupCompleted: group?.generation_status === 'completed',
  });

  // `loading` is the knowledge fetch on open/reload. Materials are not being generated yet.
  const isPreparingMaterials =
    materialsStatus.phase === 'waiting' ||
    materialsStatus.phase === 'generating';
  // A lesson from a link can't start until its script and narration are ready.
  const nextSession = group?.sessions?.find((session) => !completedIds.has(session.id));
  const preparingLesson = nextSession && isPreparingLesson(nextSession) ? nextSession : undefined;
  const isStartBusy = isPreparingMaterials || !!preparingLesson || isStarting || isCustomerLoading;

  if (isLoading) {
    return (
      <div className="relative isolate min-h-screen">
        <div className="relative w-full max-w-3xl mx-auto space-y-6 sm:space-y-8 py-6 sm:py-8 px-4 animate-pulse">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate({ to: '/dashboard' })}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors min-h-[44px]"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </button>
            <div className="p-1.5">
              <Share2 className="h-4 w-4 text-muted-foreground/30" />
            </div>
          </div>

          <div className="relative h-44 sm:h-56 rounded-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-zinc-100 via-zinc-50 to-primary/5" />
            <div className="absolute inset-0 bg-gradient-to-t from-white/55 via-white/20 to-transparent" />
            <div className="relative z-10 flex h-full flex-col justify-end p-4 sm:p-6">
              <div className="h-7 w-3/4 rounded-lg bg-zinc-200/70" />
              <div className="mt-3 flex items-center gap-3">
                <span className="h-4 w-16 rounded-full bg-zinc-200/70" />
                <span className="h-4 w-20 rounded-full bg-zinc-200/70" />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="h-3.5 w-full rounded bg-muted/50" />
            <div className="h-3.5 w-5/6 rounded bg-muted/50" />
            <div className="h-3.5 w-2/3 rounded bg-muted/50" />
          </div>

          <div className="w-full h-[38px] bg-stone-100 rounded-full border border-stone-200/50" />

          <div className="space-y-4">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Sessions
            </h2>
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-3.5 rounded-xl border border-transparent bg-muted/30"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold bg-muted text-muted-foreground">
                    {i}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1.5 py-0.5">
                    <div className="h-4 rounded bg-muted/60" style={{ width: `${50 + ((i * 17) % 35)}%` }} />
                    <div className="h-3 rounded bg-muted/40" style={{ width: `${65 + ((i * 23) % 30)}%` }} />
                    <div className="h-3 w-12 rounded bg-muted/40" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              What you'll achieve
            </h2>
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-green-600/25 mt-0.5 flex-shrink-0" />
                  <div className="h-3.5 rounded bg-muted/45 mt-0.5" style={{ width: `${55 + ((i * 19) % 40)}%` }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-sm text-muted-foreground">Learning path not found</p>
      </div>
    );
  }



  // Find first uncompleted session
  const nextSessionId = group.sessions.find((s) => !completedIds.has(s.id))?.id;
  const isOwner = !!user && !!group.owner_id && user.id === group.owner_id;
  const isLearningGroup = (group.type ?? 'learning') === 'learning';
  const activeCoverImageUrl = group.cover_image_url ?? null;
  const isCoverImageLoaded = !!activeCoverImageUrl && loadedCoverImageUrl === activeCoverImageUrl;
  const useLightHeroText = isCoverImageLoaded;

  return (
    <div className="relative isolate min-h-screen">
      <AnimatePresence>
        {activeCoverImageUrl && isCoverImageLoaded && (
          <motion.div
            key={activeCoverImageUrl}
            className="pointer-events-none fixed inset-0 hidden overflow-hidden sm:block"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <img
              src={activeCoverImageUrl}
              alt=""
              className="absolute inset-[-10%] h-[120%] w-[120%] object-cover blur-[88px] saturate-[0.8] opacity-30"
            />
            <div className="absolute inset-0 bg-white/78" />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative w-full max-w-3xl mx-auto space-y-6 sm:space-y-8 py-6 sm:py-8 px-4">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate({ to: '/dashboard' })}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors min-h-[44px]"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/learn/${group.public_id}`);
                toast.success('Link copied');
              }}
              className="p-1.5 rounded-md hover:bg-muted transition-colors"
            >
              <Share2 className="h-4 w-4" />
            </button>
            {isOwner && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1.5 rounded-md hover:bg-muted transition-colors">
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() =>
                      navigate({ to: '/session-groups/$id', params: { id: group.id } })
                    }
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit group
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      visibilityMutation.mutate({
                        groupId: group.id,
                        isPublished: !group.is_published,
                      })
                    }
                    disabled={visibilityMutation.isPending}
                  >
                    {group.is_published ? (
                      <>
                        <EyeOff className="h-4 w-4 mr-2" />
                        Hide
                      </>
                    ) : (
                      <>
                        <Globe className="h-4 w-4 mr-2" />
                        Publish
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onClick={() => setDeleteTarget({ id: group.id, name: group.name })}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Hero */}
        <div className="relative h-44 sm:h-56 rounded-2xl overflow-hidden" style={{ viewTransitionName: 'learning-path-hero' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-white via-zinc-100 to-primary/10" />
          <AnimatePresence mode="wait">
            {useSoftHero ? (
              <motion.div
                key="generating"
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8 }}
              >
                <GeneratingHeroOverlay />
              </motion.div>
            ) : group.cover_image_url ? (
              <>
                <motion.div
                  key="cover-placeholder"
                  className="absolute inset-0 bg-gradient-to-br from-zinc-100 via-zinc-50 to-primary/10"
                  initial={{ opacity: 0.65 }}
                  animate={{ opacity: isCoverImageLoaded ? 0 : 0.65 }}
                  transition={{ duration: 0.45, ease: 'easeOut' }}
                />
                <motion.img
                  key="cover"
                  src={group.cover_image_url}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: isCoverImageLoaded ? 1 : 0 }}
                  transition={{ duration: 0.7, ease: 'easeOut' }}
                  onLoad={() => setLoadedCoverImageUrl(group.cover_image_url)}
                />
              </>
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-white via-zinc-100 to-primary/10" />
            )}
          </AnimatePresence>
          <div className={`absolute inset-0 ${useSoftHero ? 'bg-white/10' : isCoverImageLoaded ? 'bg-gradient-to-t from-black/55 via-black/20 to-black/10' : 'bg-gradient-to-t from-white/55 via-white/20 to-transparent'}`} />
          {isOwner && !isInProgress && (
            <CoverImageUpload groupId={group.id} currentImageUrl={activeCoverImageUrl} />
          )}
          <div className={`relative z-10 flex h-full flex-col justify-end p-4 sm:p-6 ${useLightHeroText ? 'text-white' : 'text-zinc-900'}`}>
            <AnimatePresence mode="wait">
              {isInProgress && !group.name ? (
                <motion.div
                  key="skeleton"
                  className="space-y-2.5"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -5 }}
                >
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-3/4 rounded-lg bg-zinc-200/90 sm:h-7" />
                    <div className="flex gap-3">
                      <Skeleton className="h-4 w-16 rounded-full bg-zinc-200/80" />
                      <Skeleton className="h-4 w-20 rounded-full bg-primary/15" />
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="content"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <h1 className="text-xl sm:text-2xl font-bold tracking-tight">{group.name}</h1>
                  <div className={`mt-2 flex flex-wrap items-center gap-3 text-xs sm:gap-4 sm:text-sm ${useLightHeroText ? 'text-white/80' : 'text-zinc-600'}`}>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {group.total_duration_minutes} min
                    </span>
                    <span className="flex items-center gap-1">
                      <BookOpen className="h-3.5 w-3.5" />
                      {group.session_count} sessions
                    </span>
                    {group.enrollment_count > 0 && (
                      <span>{group.enrollment_count} enrolled</span>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Description */}
        <div className="space-y-3">
          {isInProgress && !group.description ? (
            <div className="space-y-2">
              <motion.div
                className="h-3.5 w-full rounded bg-muted"
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
              <motion.div
                className="h-3.5 w-5/6 rounded bg-muted"
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 1.5, repeat: Infinity, delay: 0.15 }}
              />
              <motion.div
                className="h-3.5 w-2/3 rounded bg-muted"
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground leading-relaxed">{group.description}</p>
          )}
        </div>

        {/* Materials / generation status banner */}
        {isInProgress ? (
          <MaterialsStatusBanner
            status={{
              phase: 'waiting',
              title: `Building session ${group.generated_session_count + 1} of ${group.target_session_count}`,
              subtitle: 'Your sessions are being generated. This usually takes a minute or two.',
            }}
          />
        ) : materialsStatus.showBanner ? (
          <MaterialsStatusBanner
            status={{
              phase: materialsStatus.phase,
              title: materialsStatus.bannerTitle,
              subtitle: materialsStatus.bannerSubtitle,
            }}
          />
        ) : null}

        {/* Failed state */}
        {isFailed && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-700">
              Something went wrong while generating this learning path. Please try again.
            </p>
          </div>
        )}

        {/* Start / Continue — learner CTA. Owners of screening/research groups use Candidates/Edit per session. */}
        {nextSessionId && !isInProgress && !isFailed && (isLearningGroup || !isOwner) && (
          <StartLearningButton
            onClick={() => {
              if (isStartBusy) return;
              trackLearningPathStart();
              if (hasCredits) {
                startSession(nextSessionId);
              } else {
                navigate({ to: '/billing' });
              }
            }}
            isLoading={isStartBusy}
            loadingLabel={
              preparingLesson
                ? `${lessonPrepLabel(preparingLesson)}...`
                : isPreparingMaterials
                ? 'Preparing materials...'
                : isCustomerLoading
                  ? 'Checking access...'
                  : 'Starting...'
            }
          >
            {completedIds.size > 0 ? 'Continue learning' : 'Start learning'}
          </StartLearningButton>
        )}
        {!nextSessionId && completedIds.size > 0 && !isInProgress && isLearningGroup && (
          <div className="rounded-xl border border-green-200 bg-green-50/50 p-4 flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800">
              You've completed all sessions in this learning path
            </p>
          </div>
        )}

        {/* Sessions list */}
        <div className="space-y-4">
          {group.sessions.length > 0 && (
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Sessions
            </h2>
          )}
          <div className="space-y-2">
            {group.sessions.map((session, i) => {
              const isCompleted = completedIds.has(session.id);
              const isCurrent = session.id === nextSessionId;

              return (
                <ContextMenu key={session.id}>
                  <ContextMenuTrigger asChild>
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.05 }}
                      className={`group flex items-start gap-3 p-3.5 rounded-xl border transition-all duration-300 ${isCurrent ? 'border-stone-200 bg-stone-50/50' : 'border-transparent bg-muted/30'
                        } ${isCompleted ? 'opacity-80' : ''}`}
                    >
                      <div
                        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${isCompleted
                          ? 'bg-green-100 text-green-700'
                          : isCurrent
                            ? 'bg-stone-100 text-stone-700'
                            : 'bg-muted text-muted-foreground'
                          }`}
                      >
                        {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{session.name}</p>
                        {session.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                            {session.description}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {session.duration} min{lessonPrepLabel(session) ? ` · ${lessonPrepLabel(session)}` : ''}
                        </p>
                      </div>
                      {isOwner && (
                        <div className="flex items-center gap-1.5 shrink-0">
                          {!isLearningGroup && (
                            <Button
                              type="button"
                              size="sm"
                              onClick={() =>
                                navigate({
                                  to: '/applications/$sessionId',
                                  params: { sessionId: session.id },
                                })
                              }
                            >
                              Candidates
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="raised"
                            size="sm"
                            className="opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
                            onClick={() =>
                              navigate({ to: '/sessions/$id', params: { id: session.id } })
                            }
                          >
                            Edit
                          </Button>
                        </div>
                      )}
                      {!isOwner && isCompleted && (
                        <div className="flex items-center gap-1 shrink-0">
                          {hasCredits && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => startSession(session.id)}
                              disabled={isStarting}
                              className="h-9 w-9 text-muted-foreground"
                            >
                              <RotateCcw className={`h-4 w-4 ${startingSessionId === session.id ? 'animate-pulse' : ''}`} />
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleViewResults(session.id)}
                            disabled={loadingResultFor === session.id}
                            className="h-9 w-9 text-muted-foreground"
                          >
                            <Eye className={`h-4 w-4 ${loadingResultFor === session.id ? 'animate-pulse' : ''}`} />
                          </Button>
                        </div>
                      )}
                      {!isOwner && isCurrent && hasCredits && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => startSession(session.id)}
                          disabled={isStarting}
                          className="h-9 w-9 shrink-0"
                        >
                          <Play className={`h-4 w-4 ${startingSessionId === session.id ? 'animate-pulse' : ''}`} />
                        </Button>
                      )}
                    </motion.div>
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    <ContextMenuItem
                      onClick={() => navigate({ to: '/sessions/$id', params: { id: session.id } })}
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit template
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              );
            })}
            {isInProgress && group.sessions.length > 0 && group.sessions.length < (group.target_session_count || 1) &&
              Array.from({ length: (group.target_session_count || 1) - group.sessions.length }).map((_, i) => (
                <motion.div
                  key={`skeleton-${i}`}
                  className="flex items-start gap-3 p-3.5 rounded-xl bg-muted/20"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted/40 animate-pulse" />
                  <div className="flex-1 space-y-2 py-0.5">
                    <motion.div
                      className="h-3.5 rounded bg-muted/40"
                      style={{ width: `${55 + ((i * 17) % 30)}%` }}
                      animate={{ opacity: [0.3, 0.6, 0.3] }}
                      transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.15 }}
                    />
                    <motion.div
                      className="h-3 rounded bg-muted/30"
                      style={{ width: `${70 + ((i * 23) % 25)}%` }}
                      animate={{ opacity: [0.2, 0.5, 0.2] }}
                      transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.15 + 0.1 }}
                    />
                    <motion.div
                      className="h-2.5 w-12 rounded bg-muted/30"
                      animate={{ opacity: [0.2, 0.5, 0.2] }}
                      transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.15 + 0.2 }}
                    />
                  </div>
                </motion.div>
              ))
            }
          </div>
        </div>

        {/* Materials slider */}
        {group.sessions.length > 0 && materialsStatus.isLoading && (
          <div className="space-y-3">
            <div className="h-4 w-24 rounded bg-muted/50" />
            <div className="flex gap-3 overflow-hidden">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 w-48 flex-shrink-0 rounded-xl bg-muted/40 animate-pulse" />
              ))}
            </div>
          </div>
        )}
        {group.sessions.length > 0 && !materialsStatus.isLoading && materialsStatus.hasDisplayableMaterials && (
          <MaterialsSlider
            sessionMaterials={materialsStatus.sessionMaterials}
            totalImages={materialsStatus.totalImages}
            totalProcessingImages={materialsStatus.totalProcessingImages}
          />
        )}

        {/* Learning outcomes — only meaningful for learning paths, not screening/research groups */}
        {(group.type ?? 'learning') === 'learning' && group.learning_outcomes.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              What you'll achieve
            </h2>
            <div className="space-y-2">
              {group.learning_outcomes.map((outcome, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">{outcome}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Session results sheet */}
        {viewingCandidate && createPortal(
          <Dialog open={!!viewingApplicationId} onOpenChange={(open) => !open && setViewingApplicationId(null)}>
            <CandidateDetailsSheet
              isOpen={!!viewingApplicationId}
              onClose={() => setViewingApplicationId(null)}
              candidates={[viewingCandidate]}
              currentCandidateIndex={0}
              onNavigate={() => { }}
              isLearnerView
            />
          </Dialog>,
          document.body,
        )}

        {/* Delete confirmation dialog */}
        <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete learning path</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  if (deleteTarget) {
                    deleteMutation.mutate({ path: { group_id: deleteTarget.id } });
                  }
                }}
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
